import React from 'react';
import Icon from '../Icon/Icon';

/**
 * Reusable Button component with multiple variants
 * @param {string} variant - 'primary' | 'secondary' | 'ghost' | 'outline' | 'white' | 'gold'
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {string} icon - Optional icon name (Material Symbols)
 * @param {string} iconPosition - 'left' | 'right'
 * @param {boolean} fullWidth - Whether button should be full width
 * @param {boolean} loading - Loading state
 * @param {boolean} shine - Add shine effect on hover
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'right',
  fullWidth = false,
  loading = false,
  shine = false,
  className = '',
  ...props
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 
    font-bold tracking-wide rounded-xl
    transition-all duration-200 
    active:scale-[0.98] 
    disabled:opacity-50 disabled:cursor-not-allowed
    group relative overflow-hidden
  `;

  const variants = {
    primary: `
      bg-primary hover:bg-primary-600 text-white
      shadow-[0_8px_25px_-8px_rgba(13,108,242,0.6)]
      ring-1 ring-white/10
    `,
    secondary: `
      bg-white/5 hover:bg-white/10 
      backdrop-blur-md border border-white/10 
      text-white
    `,
    ghost: `
      bg-transparent hover:bg-white/5
      text-white
    `,
    outline: `
      bg-transparent border border-white/20 
      hover:border-primary/50 hover:bg-white/5
      text-white
    `,
    white: `
      bg-white text-black hover:bg-gray-200
    `,
    gold: `
      bg-gold hover:bg-gold-light text-black
      shadow-[0_8px_25px_-8px_rgba(212,175,55,0.6)]
    `,
    danger: `
      bg-red-500 hover:bg-red-600 text-white
      shadow-[0_8px_25px_-8px_rgba(239,68,68,0.6)]
    `,
  };

  const sizes = {
    sm: 'h-10 px-4 text-xs',
    md: 'h-12 px-6 text-sm',
    lg: 'h-14 px-8 text-base',
  };

  const iconSizes = {
    sm: 'sm',
    md: 'sm',
    lg: 'md',
  };

  return (
    <button
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={loading}
      {...props}
    >
      {loading && (
        <span className="animate-spin">
          <Icon name="progress_activity" size={iconSizes[size]} />
        </span>
      )}
      
      {icon && iconPosition === 'left' && !loading && (
        <Icon 
          name={icon} 
          size={iconSizes[size]} 
          className="group-hover:-translate-x-0.5 transition-transform"
        />
      )}
      
      <span className="relative z-10">{children}</span>
      
      {icon && iconPosition === 'right' && !loading && (
        <Icon 
          name={icon} 
          size={iconSizes[size]} 
          className="relative z-10 group-hover:translate-x-0.5 transition-transform"
        />
      )}

      {/* Shine effect */}
      {shine && (
        <div className="absolute top-0 -left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-shine" />
      )}
    </button>
  );
};

export default Button;
